import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { UpdateKolliButton } from './UpdateKolliButton';
import './OrderScannerTable.scss';
import { HubSortListOrderEntry } from '../../hooks/useFetchHubSortList';
import { setMultipleOrderDeviation } from '../../utils/deviationsUpdate';
import { CellMeasurerCache, List } from 'react-virtualized';
import { CheckboxComponent } from '../../ReusableComponents/Checkbox/CheckboxComponent';

export function setColorByStatus(
  scannedPackages: number,
  packagesNumber: number
): string {
  if (scannedPackages === packagesNumber && packagesNumber > 0) {
    return '#0eb227';
  } else if (scannedPackages < packagesNumber && scannedPackages > 0) {
    return '#ffe200';
  } else if (scannedPackages === 0) {
    return '#dd4912';
  } else {
    return '#FF8AFE';
  }
}

function TableLoadedRow({
  orderEntry,
  addRemoveOrders,
  selectedOrders,
  updateHubSortListEntries,
  bundleId,
}: {
  orderEntry: HubSortListOrderEntry;
  addRemoveOrders: Dispatch<SetStateAction<any[]>>;
  selectedOrders: string[];
  updateHubSortListEntries: (bundlesId: string[]) => Promise<void>;
  bundleId: string;
}) {
  const {
    order,
    scannedParcelCount,
    partnerScanCount,
    preciseParcelCount,
    partnerScansConfirmed,
  } = orderEntry;
  const clickHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    if (selectedOrders.includes(value)) {
      addRemoveOrders(selectedOrders.filter(id => id !== value));
    } else {
      addRemoveOrders([...selectedOrders, value]);
    }
  };
  return (
    <tr
      key={`'screen'${order.id} ${Math.random()}`}
      data-order-id={`'screen'${order.id}`}
    >
      <td
        style={{
          borderLeftColor: setColorByStatus(
            scannedParcelCount,
            preciseParcelCount
          ),
        }}
      >
        <a
          href={`/orders/${order.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="link__clean"
        >
          {order.id}
        </a>
      </td>
      <td> {`${order.packagesNumber}`}</td>
      <td
        style={{
          color: partnerScansConfirmed ? '#0eb227' : '#dd4912',
        }}
      >
        {`${partnerScanCount}`}
      </td>
      <td> {`${scannedParcelCount}/${preciseParcelCount}`}</td>
      <td>{order.senderName}</td>
      <td>
        <UpdateKolliButton
          nrOfParcels={order.packagesNumber}
          parcelCounted={scannedParcelCount}
          orderId={order.id}
          updateHubSortListEntries={updateHubSortListEntries}
          bundleId={bundleId}
        />
      </td>
      <td>
        <CheckboxComponent
          checked={selectedOrders.includes(order.id)}
          handleChange={clickHandler}
          value={order.id}
          checkBoxClassName="sort-list__delete-order-checkbox"
          name="sort-list-delete-order-checkbox"
        />
      </td>
      <td />
    </tr>
  );
}

export function OrderScannerTable({
  orders = [],
  updateHubSortListEntries,
  bundleId,
  onlyRemaining,
  cache,
  listRef,
  virtualizationIndex,
}: {
  orders: HubSortListOrderEntry[];
  updateHubSortListEntries?: (bundlesId: string[]) => Promise<void>;
  bundleId: string;
  onlyRemaining?: boolean;
  cache?: CellMeasurerCache;
  listRef?: List;
  virtualizationIndex?: number;
}): JSX.Element {
  const [selectedOrders, addRemoveOrders] = useState([]);
  const markSelectedOrdersReady = () => {
    setMultipleOrderDeviation(selectedOrders).then(() => {
      updateHubSortListEntries([bundleId])
        .then(() => console.log('update done'))
        .catch(error => console.log(error));
      addRemoveOrders([]);
    });
  };
  const renderTableWithOrders = () => {
    let dataSource: HubSortListOrderEntry[];
    if (onlyRemaining) {
      dataSource = orders.filter(o => o.scannedParcelCount === 0);
    } else {
      dataSource = orders.filter(
        o =>
          o.scannedParcelCount !== o.preciseParcelCount &&
          o.scannedParcelCount !== 0
      );
    }
    return (
      (dataSource.length > 0 &&
        dataSource.map(order => (
          <TableLoadedRow
            bundleId={bundleId}
            orderEntry={order}
            key={order.order.id}
            selectedOrders={selectedOrders}
            addRemoveOrders={addRemoveOrders}
            updateHubSortListEntries={updateHubSortListEntries}
          />
        ))) ||
      null
    );
  };

  useEffect(() => {
    if (cache && listRef) {
      cache.clear(virtualizationIndex, 0);
      listRef.forceUpdateGrid();
    }
  }, [selectedOrders, cache, listRef, virtualizationIndex]);

  return (
    <div className="u-pbl">
      <table className="order-scanner-table">
        <thead>
          <tr>
            <th data-sort="id" data-sortmethod="number">
              Order ID
            </th>
            <th data-sort="id" data-sortmethod="number">
              Parcels
            </th>
            <th data-sort="id" data-sortmethod="number">
              Retailer
            </th>
            <th data-sort="id" data-sortmethod="number">
              Hub
            </th>
            <th data-sort="delivery_name" data-sortmethod="string">
              Sender
            </th>
            <th data-sort="delivery_name" data-sortmethod="string">
              Update Parcels
            </th>
            <th data-sort="delivery_name" data-sortmethod="string">
              Remove
            </th>
            <th />
          </tr>
        </thead>
        <tbody>{renderTableWithOrders()}</tbody>
      </table>
      {selectedOrders.length > 0 ? (
        <div
          className={'order-scanner-table-remove-button'}
          onClick={markSelectedOrdersReady}
        >
          <p>
            Remove {selectedOrders.length} order(s)? <span>CONFIRM</span>
          </p>
        </div>
      ) : null}
    </div>
  );
}
